/**
 * Edit one article.
 *
 * The save carries the `version` this screen loaded. If someone else saved in
 * between, the server refuses rather than overwriting them, and this screen says
 * so instead of pretending the save worked - two people editing one clinic's
 * drafts is the normal case, not an edge one.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Link,
  useBlocker,
  useLoaderData,
  useLocation,
  useNavigate,
  useRevalidator,
} from 'react-router';
import {
  ApiError,
  del,
  get,
  patch,
  post,
  type ArticleDetail,
  type ArticleReview,
  type CatalogItem,
  type GenerationContext,
  type ListResponse,
  type RewriteResult,
} from '../api.ts';
import { Card, Empty, Pill } from '../components/ui.tsx';
import { StageBar } from '../components/StageBar.tsx';
import { Field } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { DateInput } from '../components/DateInput.tsx';
import { clearDraft, isRecoverable, readDraft, saveDraft } from '../draft.ts';
import { InsertBlocks } from '../components/InsertBlocks.tsx';
import { ArticleImages } from '../components/ArticleImages.tsx';
import { LegalReview } from '../components/LegalReview.tsx';
import { OutlineRail } from '../components/OutlineRail.tsx';
import { RichEditor } from '../components/RichEditor.tsx';
import { RewritePopover } from '../components/RewritePopover.tsx';
import { toHtml, toInlineHtml } from '../rich-text.ts';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { locate, replaceRange } from '../outline.ts';
import { findPassage, sentenceAround } from '../find-passage.ts';
import { problemCount, reviewDraft, type EditorialCheckRow } from '../editorial.ts';
import {
  MaterialPicker,
  materialFrom,
  materialPayload,
  type MaterialSelection,
} from '../components/MaterialPicker.tsx';

/** A finding handed to the rewrite as the thing to resolve. */
interface RewriteFixInput {
  title: string;
  guidance?: string;
  citation?: string;
}

/** The same three the creation wizard offers; the article may change its mind. */
const ARTICLE_TYPES = [
  { value: 'blog', label: 'ブログ記事' },
  { value: 'column', label: 'コラム' },
  { value: 'case', label: '症例紹介' },
];

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: '下書き' },
  { value: 'IN_REVIEW', label: '要確認' },
  { value: 'APPROVED', label: '承認済み' },
];

/** Enum names are for the database; the panel shows what they mean. */
const CATEGORY_LABEL: Record<string, string> = {
  STRENGTH: '強み',
  TREATMENT_POLICY: '施術方針',
  COUNSELING_POLICY: 'カウンセリング方針',
  EQUIPMENT: '設備',
};

/** What `missing` names, said in the operator's language. */
const MISSING_LABEL: Record<string, string> = {
  persona: 'ペルソナが未選択です',
  treatment: '施術が未選択です',
  cta: 'CTAが未選択です',
  supervisor: '承認済みの監修医がいません',
  'clinic.siteUrl': 'クリニックのサイトURLが未登録です',
  'clinic.siteName': 'クリニックのサイト名が未登録です',
};

export default function ArticleEdit() {
  const loaded = useLoaderData<{
    article: ArticleDetail;
    context: GenerationContext;
    personas: ListResponse<CatalogItem>;
    treatments: ListResponse<CatalogItem>;
    supervisors: ListResponse<CatalogItem>;
    ctaTemplates: ListResponse<CatalogItem>;
    editorialChecks: { items: EditorialCheckRow[] };
  }>();
  const { article, context } = loaded;
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [title, setTitle] = useState(article.title);
  const [bodyMd, setBodyMd] = useState(article.body_md);
  const [status, setStatus] = useState(article.status);
  const [publishOn, setPublishOn] = useState(article.publish_on ?? '');
  const [keywords, setKeywords] = useState<string[]>(article.keywords);
  /*
   * Held as text, not as a number. An input the writer is clearing passes
   * through '' on its way to empty, and a number-typed state has to represent
   * that as NaN or 0 - both of which mean something else.
   */
  const [targetLength, setTargetLength] = useState(
    article.target_length === null ? '' : String(article.target_length),
  );
  /*
   * Take the reader to the fragment they navigated to.
   *
   * A browser scrolls to `#brief` on a full page load; a client-side navigation
   * changes the URL and leaves the scroll position alone, so 条件 in the stage
   * bar would move the address bar and nothing else. Re-run on every hash change
   * because pressing 条件 twice is a thing people do when the first press
   * appeared to do nothing.
   */
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === '') return;
    const target = document.getElementById(hash.slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  /*
   * The four references, as one piece of state.
   *
   * '' is "none", which the save turns into an explicit null - the API's
   * distinction between clearing a field and leaving it alone is the reason the
   * empty option can exist at all.
   */
  /*
   * The two brief fields that used to be set once, at creation, and never again.
   *
   * 記事タイプ and 文体 are read by every generation this article runs, so an
   * article created as a ブログ記事 in the wrong tone had to be recreated to
   * change either - which threw away the outline and the drafted sections with
   * them. The API accepted both all along; only this screen did not offer them.
   */
  const [articleType, setArticleType] = useState(article.article_type || 'blog');
  const [tone, setTone] = useState(article.tone ?? '');
  const [material, setMaterial] = useState<MaterialSelection>(materialFrom(article));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /*
   * The version this screen is allowed to write against.
   *
   * Held in state rather than read from the loader, because autosave writes
   * repeatedly: every successful PATCH advances the version, and a second save
   * carrying the version the page loaded with would be refused as stale. The
   * loader's copy only seeds it.
   */
  const [version, setVersion] = useState(article.version);
  /** What the server currently holds, so "unsaved" is a fact rather than a flag. */
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify({ title: article.title, bodyMd: article.body_md, keywords: article.keywords }),
  );
  const [autosaving, setAutosaving] = useState(false);
  /** A draft found in storage that is newer than the article, offered not applied. */
  const [recovered, setRecovered] = useState(() => {
    const stored = readDraft(article.id);
    return isRecoverable(stored, {
      title: article.title,
      bodyMd: article.body_md,
      keywords: article.keywords,
      version: article.version,
    })
      ? stored
      : null;
  });
  /*
   * NO WHOLE-ARTICLE GENERATION HERE any more.
   *
   * 「AI生成」 offered 構成案を生成 and 本文を生成 on the finishing screen. Both were
   * settled two screens earlier - the plan screen decides the outline and the
   * write screen drafts every section against it - so the buttons could only do
   * one of two things: repeat work already done, or replace a body somebody had
   * been editing with a fresh one written from the same brief. The section-level
   * redraw on the outline rail is the version of this that knows what it is
   * replacing, and it stays.
   */
  /*
   * The stored legal review. Loaded with the screen rather than on demand: it is
   * already paid for, and a verdict nobody sees until they press a button is a
   * verdict nobody sees.
   */
  const [review, setReview] = useState<ArticleReview>({
    review: null,
    corpus: null,
    stale: false,
    staleReason: null,
    articleVersion: article.version,
  });

  /*
   * The editor itself, because three operations are about a place inside the
   * document: inserting a block at the caret, jumping to a section, and putting
   * a rewrite back where the selection was.
   *
   * A textarea answered those with character offsets into the Markdown. A
   * document has no such offsets - its positions are into its own node tree - so
   * the operations are expressed in the document's own terms instead of being
   * translated back and forth, which is where an off-by-one silently eats a
   * sentence.
   */
  const editorRef = useRef<TiptapEditor | null>(null);
  /** Which section is being redrawn from the rail, by ordinal. */
  const [redrawing, setRedrawing] = useState<number | null>(null);
  /*
   * What is selected, as text.
   *
   * Offsets into a Markdown string were the right answer for a textarea and are
   * the wrong one for a document: the editor's positions are into its own node
   * tree, and the rewrite endpoint wants the passage anyway. So the selection is
   * carried as the words themselves.
   */
  /** Which material panel is open beside the editor, if any. */
  const [dock, setDock] = useState<'images' | 'blocks' | null>(null);
  const [selectedText, setSelectedText] = useState('');
  /*
   * Where the selected passage is on screen, so the rewrite controls can sit
   * beside it. Null whenever nothing is selected, which is also what closes the
   * panel - one source of truth for "is there a passage to work on".
   */
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [style, setStyle] = useState('');
  const [rewriteNote, setRewriteNote] = useState('');
  const [rewriting, setRewriting] = useState(false);
  /*
   * A rewrite's own failure, kept apart from the screen's error strip: it is
   * about the passage under the popover, and that is where it has to be read.
   */
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const [rewritten, setRewritten] = useState<RewriteResult | null>(null);
  /** The finding the open rewrite is resolving, if it came from 「AIで直す」. */
  const fixRef = useRef<{
    selection: string;
    fix: RewriteFixInput;
    context: { before?: string; after?: string };
  } | null>(null);

  const selected = selectedText;

  /**
   * What surrounds a passage in the stored text.
   *
   * The rewrite is given its neighbours so the replacement fits where it lands
   * instead of re-introducing the paragraph above. Found by locating the passage
   * in the Markdown: the document knows where the selection is in its own tree,
   * and this is the one place the two views have to be reconciled. A passage that
   * cannot be found - one spanning table cells, say - sends no context at all,
   * because the wrong context is worse than none.
   */
  function neighbours(body: string, passage: string): { before?: string; after?: string } {
    const at = body.indexOf(passage.trim());
    if (at === -1) return {};
    return {
      before: body.slice(Math.max(0, at - 600), at),
      after: body.slice(at + passage.trim().length, at + passage.trim().length + 600),
    };
  }

  /**
   * Rewrite the selected passage.
   *
   * The selection is sent as text with a little of what surrounds it, not as
   * offsets: this textarea may hold unsaved edits, so offsets into the stored
   * body would cut the wrong words. The neighbours go along so the replacement
   * fits where it lands instead of re-introducing the paragraph above.
   */
  async function runRewrite(override?: {
    selection?: string;
    instruction?: string;
    /** The words either side, when the caller already knows them. */
    context?: { before?: string; after?: string };
    fix?: RewriteFixInput;
  }) {
    /*
     * The passage may come from a finding rather than from a highlight.
     *
     * Passed in rather than read from state, because a fix runs immediately
     * after selecting the sentence and React has not re-rendered yet - reading
     * `selected` there would rewrite whatever was selected BEFORE, which is the
     * worst possible bug for a button that replaces text.
     */
    const passage = override?.selection ?? selected;
    const note = override?.instruction ?? rewriteNote;
    /*
     * A fix survives 「やり直す」. The popover's own 書き直す calls this with no
     * override, and without the remembered finding a retry of a fix silently
     * became a plain rewrite of the sentence with nothing to resolve.
     */
    const pendingFix = fixRef.current?.selection === passage ? fixRef.current : null;
    const fix = override?.fix ?? pendingFix?.fix;
    const context = override?.context ?? pendingFix?.context;
    if (passage.trim() === '') return;
    setRewriting(true);
    setRewriteError(null);
    setRewritten(null);
    try {
      setRewritten(
        await post<RewriteResult>(`/articles/${article.id}/rewrite`, {
          selection: passage,
          /*
           * The neighbours, found by locating the passage in the stored text.
           * The document knows where the selection is in its own tree; the API
           * wants the words around it, and this is the one place the two views
           * have to be reconciled. A passage that cannot be found - because it
           * spans a table cell, say - sends no context rather than the wrong
           * context.
           */
          ...(context ?? neighbours(bodyMd, passage)),
          ...(style ? { style } : {}),
          ...(note.trim() ? { instruction: note.trim() } : {}),
          ...(fix ? { fix } : {}),
        }),
      );
    } catch (err) {
      setRewriteError(
        err instanceof ApiError
          ? err.code === 'prompt_not_configured'
            ? 'この生成に使う指示文がまだ保存されていません。JMCにプロンプトの設定を依頼してください。'
            : err.status === 409
            ? 'リライトにはペルソナが必要です。下の「記事の条件と使用情報」で選んでください。'
            : err.code === 'quota_exceeded'
              ? '今月の生成上限に達しています。'
              : err.code === 'rate_limited'
                ? 'AIサービスが混み合っています。しばらく待って再試行してください。'
                : (err.fields[0] ?? err.message)
          : 'リライトできませんでした',
      );
    } finally {
      setRewriting(false);
    }
  }

  /** Put the replacement where the selection was. The save is still the author's. */
  function applyRewrite() {
    if (!rewritten || selected === '') return;
    /*
     * Replaced in the document rather than in the Markdown: the editor holds the
     * live text, and writing into the string behind it would be overwritten by
     * the next keystroke. `insertContent` replaces the current selection, which
     * is still the passage the rewrite was asked for.
     */
    const inline = toInlineHtml(rewritten.text);
    editorRef.current
      ?.chain()
      .focus()
      .insertContent(inline ?? toHtml(rewritten.text))
      .run();
    setRewritten(null);
    fixRef.current = null;
  }

  /**
   * Insert a block at the caret.
   *
   * Blank lines around it, because markdown needs them to start a new block and
   * a writer dropping a CTA into the middle of a paragraph means it to be its own
   * paragraph.
   */
  function insertAtCaret(markdown: string) {
    // Converted first: the blocks are written as Markdown by the code that
    // builds them (a CTA, a supervisor, a link), and the document takes HTML.
    editorRef.current?.chain().focus().insertContent(toHtml(markdown)).run();
  }

  /**
   * Put a picture under the heading it was drawn for.
   *
   * A picture generated for 「施術当日の流れ」 belongs in that section, and the
   * screen already knows which one it is - the writer chose it before pressing
   * 生成. Inserting at the caret instead meant the picture landed wherever the
   * cursor happened to be, which after generating one is usually nowhere near
   * the section it depicts: the writer had to scroll, click, and insert again.
   *
   * Placed after the heading's own paragraph rather than immediately under the
   * heading, because a picture between a heading and its first sentence
   * separates the two things a reader pairs. Falls back to the caret when the
   * heading cannot be found in the document - which is what happens once
   * somebody has renamed it, and is exactly when guessing would be wrong.
   */
  function insertUnderHeading(markdown: string, heading: string) {
    const editor = editorRef.current;
    if (!editor || heading.trim() === '') {
      insertAtCaret(markdown);
      return;
    }

    /*
     * Found in the DOCUMENT, not in the stored Markdown: the editor is what is
     * being written into, and its positions are the only ones an insert can use.
     */
    let at: number | null = null;
    let seenHeading = false;
    editor.state.doc.descendants((node, pos) => {
      if (at !== null) return false;
      if (node.type.name === 'heading' && node.textContent.trim() === heading.trim()) {
        seenHeading = true;
        return false;
      }
      // The first paragraph after it - insert past that.
      if (seenHeading && node.type.name === 'paragraph') {
        at = pos + node.nodeSize;
        return false;
      }
      return true;
    });

    if (at === null) {
      insertAtCaret(markdown);
      return;
    }
    editor.chain().focus().insertContentAt(at, toHtml(markdown)).run();
  }

  /**
   * Run the compliance check on the saved text.
   *
   * On the saved text, not on what is in the textarea: the rules run server-side
   * against the article row, so a report always describes something that exists.
   * The version is shown beside the verdict, which is how an operator sees that
   * they need to save before re-checking.
   */
  /*
   * The review, on open and after every write to it. Failure is silent on
   * purpose: a review that could not be fetched is not an editing error, and the
   * panel says plainly that nothing has been run.
   */
  useEffect(() => {
    let live = true;
    void get<ArticleReview>(`/articles/${article.id}/review`)
      .then((loaded) => {
        if (live) setReview(loaded);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [article.id]);

  /**
   * Go to a passage, by its words.
   *
   * Everything that wants to move the writer somewhere - a section in the rail, a
   * sentence a legal finding names - knows the TEXT it is about. A character
   * offset into the Markdown would have to be translated into the document's own
   * positions, and an off-by-one there selects the wrong sentence and then
   * rewrites it.
   *
   * Selected rather than merely scrolled to, because the tools below act on a
   * selection and asking somebody to select what they just jumped to is a second
   * gesture for one intention.
   */
  function jumpToText(
    text: string,
    options: { sentence?: boolean } = {},
  ): { text: string; before: string; after: string } | null {
    const editor = editorRef.current;
    if (!editor) return null;

    /*
     * The document as one string, with a map back to editor positions.
     *
     * Built rather than searched node by node, because a quote regularly spans
     * two text nodes - a sentence with a bolded price in the middle is three -
     * and the per-node search this replaced could only ever find a passage that
     * happened to live inside a single one.
     */
    /*
     * Text nodes of ONE paragraph are joined with nothing; a new block starts
     * with '\n'. They used to be joined with '\n' throughout, which put a line
     * break either side of every bolded price - so a quote spanning one never
     * matched exactly, and a sentence could not be told from a paragraph.
     */
    const pieces: Array<{ text: string; pos: number; sep: string }> = [];
    let block: unknown = null;
    editor.state.doc.descendants((node, pos, parent) => {
      if (node.isText && node.text) {
        pieces.push({ text: node.text, pos, sep: pieces.length > 0 && parent !== block ? '\n' : '' });
        block = parent;
      }
      return true;
    });
    const flat = pieces.map((piece) => piece.sep + piece.text).join('');

    const found = findPassage(flat, text);
    if (!found) {
      /*
       * Said out loud rather than returned silently.
       *
       * This used to `return` when it found nothing, which is why 該当箇所へ移動
       * on a legal finding was indistinguishable from a dead button: the quote
       * comes back as an excerpt with 「…」 and collapsed whitespace, it never
       * matched, and nothing said so. findPassage handles that shape now, and
       * the remaining reason to fail is the real one - the sentence has been
       * edited since the check ran.
       */
      setError('この指摘の文が本文に見つかりません。チェック後に本文が編集された可能性があります。');
      return null;
    }
    /*
     * Widened from the MIDDLE of the match, not its edges. A check's excerpt is
     * the hit with twenty characters either side, so its edges routinely reach
     * into the neighbouring sentence - widening from them selected two
     * sentences and the fix rewrote one that was fine. The middle is inside
     * the hit itself.
     */
    const middle = Math.floor((found.from + found.to) / 2);
    const range = options.sentence ? sentenceAround(flat, { from: middle, to: middle + 1 }) : found;

    /** A flat offset, back into the editor's own coordinates. */
    const positionOf = (offset: number): number | null => {
      let seen = 0;
      for (const piece of pieces) {
        seen += piece.sep.length;
        if (offset <= seen + piece.text.length) return piece.pos + Math.max(0, offset - seen);
        seen += piece.text.length;
      }
      return null;
    };

    const from = positionOf(range.from);
    const to = positionOf(range.to);
    if (from === null || to === null) return null;

    setError(null);
    editor.chain().focus().setTextSelection({ from, to }).run();

    /*
     * Scrolled smoothly, and to the MIDDLE.
     *
     * ProseMirror's own `scrollIntoView()` jumps, and stops as soon as the
     * position is barely on screen - so arriving from a finding put the sentence
     * hard against the top or bottom edge with no surrounding text. Scrolling
     * the element centres the passage, and every scrollable ancestor - the sheet
     * and the page - animates together.
     *
     * In a frame of its own, after the selection has been drawn: called in the
     * same tick, the browser is asked to scroll to a node whose position the
     * editor is still updating.
     *
     * `prefers-reduced-motion` is honoured by the browser for `smooth`, so there
     * is no branch here for it.
     */
    requestAnimationFrame(() => {
      const dom = editor.view.domAtPos(from).node;
      const element = dom instanceof HTMLElement ? dom : dom.parentElement;
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return {
      text: flat.slice(range.from, range.to),
      before: flat.slice(Math.max(0, range.from - 600), range.from),
      after: flat.slice(range.to, range.to + 600),
    };
  }

  /**
   * Turn a finding into a repair.
   *
   * A check that can only say what is wrong leaves the writer to work out what
   * to write instead - which, for 「比較優良広告にあたるおそれ」 with a provision
   * quoted underneath it, is asking a clinic's marketing assistant to interpret
   * the 医療広告ガイドライン. They have the finding, the guidance and the cited
   * provision on screen; all three are exactly what a rewrite needs to be told.
   *
   * So: select the sentence, hand the reason to the rewrite, and put the result
   * in the same popover a hand-made rewrite lands in. NOTHING IS APPLIED - the
   * writer reads the replacement beside the original and presses 置き換える, or
   * closes it and decides the finding was acceptable. The judgement stays theirs;
   * what changes is that they now have something to judge.
   */
  function fixFinding(input: {
    quote: string;
    title: string;
    guidance?: string;
    citation?: { publisher: string; title: string; excerpt: string } | null;
  }) {
    const passage = input.quote.trim();
    if (passage === '') return;
    /*
     * Selected first, and only then rewritten.
     *
     * The selection is what anchors the panel and what `applyRewrite` replaces,
     * so a fix that ran without one would propose a replacement for a passage
     * the writer cannot see and could not apply. If the sentence is no longer in
     * the article, jumpToText has already said so and there is nothing to fix.
     */
    /*
     * The WHOLE sentence, and the words either side of it as they are in the
     * editor now.
     *
     * The quote is an excerpt cut twenty characters either side of the hit, so
     * selecting it selected a fragment, and the rewrite replaced a fragment -
     * leaving half a sentence dangling each side. And `neighbours` looked the
     * excerpt up in the Markdown, where its 「…」 never matches, so the model
     * got no context at all. Both come from the document instead.
     */
    const target = jumpToText(passage, { sentence: true });
    if (!target) return;

    /*
     * The finding as its own field, not as a note. As 「追加の指示」 it sat under
     * the fence that tells the model to ignore anything touching the rules - a
     * compliance fix is exactly that - and was cut at 300 characters. The server
     * also re-checks the answer against the expression rules and tries once
     * more if the finding is still there.
     */
    const fix: RewriteFixInput = {
      title: input.title,
      ...(input.guidance ? { guidance: input.guidance } : {}),
      ...(input.citation
        ? {
            citation: `${input.citation.publisher}／${input.citation.title}「${input.citation.excerpt}」`,
          }
        : {}),
    };
    const context = { before: target.before, after: target.after };
    fixRef.current = { selection: target.text, fix, context };
    // The popover's note stays free for the writer's own words on a retry.
    setRewriteNote('');
    void runRewrite({ selection: target.text, context, fix });
  }

  /**
   * Redraw one section and splice it into the body.
   *
   * The generation writes the section row server-side; what lands in the editor
   * is unsaved text like any other edit, so the article is unchanged until the
   * writer saves. The offsets are recomputed here rather than passed in: the
   * writer may have typed since the rail rendered, and writing at a stale offset
   * is how a redraw lands in the middle of a sentence.
   */
  async function redrawSection(ordinal: number, instruction: string) {
    const ranges = locate(
      bodyMd,
      article.sections.map((section) => section.heading),
    );
    const range = ranges?.[ordinal];
    if (!range) {
      setError('本文の見出しが構成と一致しないため、この節だけの書き直しはできません。');
      return;
    }

    setRedrawing(ordinal);
    setError(null);
    try {
      const result = await post<{ text: string }>(
        `/articles/${article.id}/sections/${ordinal}/draft`,
        instruction.trim() ? { instruction: instruction.trim() } : {},
      );
      setBodyMd((current) => {
        // Recomputed against the text as it is now, for the same reason.
        const fresh = locate(
          current,
          article.sections.map((section) => section.heading),
        );
        const target = fresh?.[ordinal];
        return target ? replaceRange(current, target, result.text) : current;
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === 'prompt_not_configured'
            ? 'この生成に使う指示文がまだ保存されていません。JMCにプロンプトの設定を依頼してください。'
            : err.code === 'quota_exceeded'
            ? '今月の生成上限に達しています。'
            : (err.fields[0] ?? err.message)
          : '書き直せませんでした',
      );
    } finally {
      setRedrawing(null);
    }
  }

  /*
   * The editorial checks, recomputed as the text changes.
   *
   * No model, no request, no cost - which is why they can run on every
   * keystroke rather than behind a button. The legal check stays separate and
   * stays deliberate: it costs money and it is about the law, not the craft.
   */
  const checks = reviewDraft({
    body: bodyMd,
    /*
     * One ordered list: its head is the keyword the article targets, and the
     * rest only have to appear - see migration 050. The clinic's and store's
     * defaults follow the article's own, each once.
     */
    keywords: [...new Set([...keywords, ...context.resolved.keywords])],
    targetChars: article.target_length,
    reservationUrl: context.store.reservationUrl,
    editorialChecks: loaded.editorialChecks.items,
  });
  const problems = problemCount(checks);

  /** What the author has typed, as the snapshot "unsaved" is measured against. */
  const snapshot = JSON.stringify({ title, bodyMd, keywords });
  const dirty = snapshot !== savedSnapshot;

  /**
   * Write the form to the server.
   *
   * One path for both the button and autosave, so they cannot drift into saving
   * different things. The version comes from state and advances with each
   * successful write - autosave writes repeatedly, and the version the page
   * loaded with would be stale by the second one.
   */
  async function persist(mode: 'manual' | 'auto'): Promise<void> {
    /*
     * Checked here because the API refuses what the column refuses, and a
     * half-typed "5" would come back as a 400 the writer has to decode. The
     * number input's own min/max is advisory: it does not stop a typed value.
     */
    const length = targetLength === '' ? null : Number(targetLength);
    if (length !== null && (!Number.isInteger(length) || length < 500 || length > 20_000)) {
      setError('目安文字数は500〜20000の整数で入力してください。');
      return;
    }
    if (mode === 'manual') setSaving(true);
    else setAutosaving(true);
    setError(null);
    if (mode === 'manual') setSaved(false);
    const attempted = snapshot;
    try {
      const updated = await patch<{ version: number }>(`/articles/${article.id}`, {
        version,
        title,
        bodyMd,
        status,
        keywords,
        articleType,
        // '' is "the clinic's standing tone", which the API stores as an empty
        // override rather than as a tone of its own.
        tone,
        // Empty clears the override; the API's null does exactly that.
        targetLength: length,
        // Empty means "no date", which is a clear, not an absence.
        publishOn: publishOn === '' ? null : publishOn,
        // Same rule for the four references: '' clears, an id sets.
        ...materialPayload(material),
      });
      setVersion(updated.version);
      setSavedSnapshot(attempted);
      // It reached the server, so the local safety net is no longer needed.
      clearDraft(article.id);
      if (mode === 'manual') setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 409
            ? 'この記事は別の場所で更新されました。再読み込みしてから保存してください。'
            : (err.fields[0] ?? err.message)
          : '保存できませんでした',
      );
      // Rethrow so the autosave timer knows not to treat this as saved.
      throw err;
    } finally {
      if (mode === 'manual') setSaving(false);
      else setAutosaving(false);
    }
  }

  /*
   * The local safety net, written on every keystroke.
   *
   * Synchronous, no network, and it closes the gap autosave cannot: the seconds
   * between the last keystroke and the debounced request landing, which is
   * exactly when a crashed tab or a closed laptop takes the work.
   */
  useEffect(() => {
    if (!dirty) return;
    saveDraft(article.id, { title, bodyMd, keywords, version });
  }, [dirty, article.id, title, bodyMd, keywords, version]);

  /*
   * Autosave, three seconds after typing stops.
   *
   * Debounced rather than periodic so a burst of typing is one write, and reset
   * on every change - the timer measures "stopped typing", not "time elapsed".
   * A failure is left alone: the error is on screen, the local draft still
   * holds the text, and retrying into a 409 forever would help nobody.
   */
  useEffect(() => {
    if (!dirty || saving || autosaving) return;
    const timer = setTimeout(() => {
      void persist('auto').catch(() => undefined);
    }, 3000);
    return () => clearTimeout(timer);
  }, [dirty, saving, autosaving, snapshot]);

  /*
   * Leaving with unsaved work.
   *
   * Two different exits need two different guards: `useBlocker` catches an
   * in-app navigation, which React Router owns, and `beforeunload` catches a
   * closed tab or a reload, which it does not. Both are removed the moment the
   * work is saved, so a clean editor never nags.
   */
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      // The browser shows its own wording; assigning returnValue is what asks.
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      await persist('manual');
      // Only the deliberate save refreshes the screen: doing it on every
      // autosave would clear the read cache every few seconds for nothing.
      void revalidator.revalidate();
    } catch {
      // persist() has already put the reason on screen.
    }
  }

  async function archive() {
    setError(null);
    try {
      await del(`/articles/${article.id}`);
      void navigate('/articles');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'アーカイブできませんでした');
    }
  }

  return (
    <>
      {/* Every step, in both directions: going back to a finished step is a
          normal thing to want, and nothing is lost by it. */}
      <StageBar articleId={article.id} current={'polish'} />
      {/*
        A draft this browser stored but never got to save - a crashed tab, a
        closed laptop, a reload mid-sentence. Offered, never applied: restoring
        silently could overwrite a newer edit with older text that nobody chose.
      */}
      {recovered && (
        <Card title="保存されていない下書きが見つかりました">
          <p className="muted small" style={{ marginTop: 0 }}>
            {new Date(recovered.savedAt).toLocaleString('ja-JP')} 時点のこの端末の入力内容です。
            サーバー上の記事とは異なります。
          </p>
          <div className="editpanel__actions">
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => {
                setTitle(recovered.title);
                setBodyMd(recovered.bodyMd);
                setKeywords(recovered.keywords);
                setRecovered(null);
              }}
            >
              入力内容を復元する
            </button>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={() => {
                clearDraft(article.id);
                setRecovered(null);
              }}
            >
              破棄してサーバーの内容を使う
            </button>
          </div>
        </Card>
      )}

      {/*
        An in-app navigation with unsaved work. The blocker holds the transition
        open until the author answers, so leaving is always their decision.
      */}
      {blocker.state === 'blocked' && (
        <Card title="未保存の変更があります">
          <p className="muted small" style={{ marginTop: 0 }}>
            このページを離れると、保存していない内容は失われます。
          </p>
          <div className="editpanel__actions">
            <button
              className="btn btn--sm"
              type="button"
              disabled={saving}
              onClick={() => {
                void persist('manual')
                  .then(() => blocker.proceed?.())
                  .catch(() => undefined);
              }}
            >
              {saving ? '保存中…' : '保存して移動'}
            </button>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={() => blocker.proceed?.()}
            >
              保存せずに移動
            </button>
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => blocker.reset?.()}>
              このページに留まる
            </button>
          </div>
        </Card>
      )}

      {/*
        One form around the whole screen.

        It used to wrap only the first card, which was where every field lived.
        The article's settings now sit at the foot of the page - they are
        occasional corrections, not the first thing to meet on the way to writing
        - and they are still saved by the same 保存する button because the form
        reaches them. A second form with a second save button would be two
        answers to "did that save?".
      */}
      <form onSubmit={(event) => void save(event)}>
      <Card
        title={article.title}
        caption={`${article.store_name}・第${version}版`}
        actions={
          <>
            {article.status === 'ARCHIVED' ? (
              <Pill tone="muted">アーカイブ済み</Pill>
            ) : (
              <button className="btn btn--ghost btn--sm" type="button" onClick={() => void archive()}>
                アーカイブ
              </button>
            )}
            <Link className="btn btn--sm" to={`/articles/${article.id}/final`}>
              最終稿・書き出し
            </Link>
            <Link className="btn btn--ghost btn--sm" to="/articles">
              一覧に戻る
            </Link>
          </>
        }
      >
          {/*
            The first step, addressable.
            
            条件 and 仕上げ were the same URL, so pressing 条件 in the stage bar
            appeared to do nothing - the article's own settings were half way
            down the screen it was already on. The anchor gives step 1 a place to
            land, and every field the creation wizard asked for is now here.
          */}
          <div className="editpanel__body">
            <Field label="タイトル" htmlFor="a-title" required>
              <input
                id="a-title"
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
          </div>


          <OutlineRail
            sections={article.sections}
            body={bodyMd}
            busy={redrawing}
            onJump={jumpToText}
            onRedraw={(ordinal, instruction) => void redrawSection(ordinal, instruction)}
          />

          {/*
            The material panel, attached to the editor.

            It was two cards below the text: 画像 and 差し込み, each with its own
            heading and its own scroll position, both of them advertising that
            you could drag their tiles into the body. You could - across two
            screens of scrolling, holding the mouse down, with the drop target
            out of sight. A drag needs both ends visible at once, which means the
            material has to be beside the document rather than under it.
          */}
          <div className="form__sectionrow">
            <p className="form__section" style={{ margin: 0 }}>
              本文
            </p>
            <div className="chips">
              <button
                type="button"
                className="chip"
                aria-pressed={dock === 'images'}
                onClick={() => setDock(dock === 'images' ? null : 'images')}
              >
                画像
              </button>
              <button
                type="button"
                className="chip"
                aria-pressed={dock === 'blocks'}
                onClick={() => setDock(dock === 'blocks' ? null : 'blocks')}
              >
                リンク・差し込み
              </button>
            </div>
          </div>
          {/*
            A document, not markup.

            The people writing here are clinic staff: they see headings that look
            like headings and a table that looks like a table, and never a hash or
            an angle bracket. Markdown is still what is stored and what every
            export, check and section split reads - the conversion happens inside
            this component and nowhere else.
          */}
          <div className={`editorshell${dock ? ' editorshell--open' : ''}`}>
            <div className="editorshell__main">
              <RichEditor
                value={bodyMd}
                onChange={setBodyMd}
                onSelectionChange={(text, rect) => {
                  setSelectedText(text);
                  setSelectionRect(rect);
                  // A new selection is a new question: yesterday's answer must
                  // not still be sitting there offering to replace it.
                  if (text.trim() === '') setRewritten(null);
                }}
                editorRef={editorRef}
              />
              <span className="field__hint">
                本文を選択すると、その場で書き直しの指示ができます。書式は上のバーから。
                画像とリンクは右のパネルから本文にドラッグできます。
              </span>
            </div>

            {dock && (
              <aside className="editorshell__dock" aria-label="素材">
                <div className="editorshell__dockhead">
                  <span className="editorshell__docktitle">
                    {dock === 'images' ? '画像' : 'リンク・差し込み'}
                  </span>
                  <button
                    type="button"
                    className="rewritepop__close"
                    aria-label="パネルを閉じる"
                    onClick={() => setDock(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="editorshell__dockbody">
                  {dock === 'images' ? (
                    <ArticleImages
                      articleId={article.id}
                      coverImageId={article.cover_image_id}
                      headings={article.headings}
                      onInsert={insertAtCaret}
                      onInsertUnderHeading={insertUnderHeading}
                      onCoverChange={() => void revalidator.revalidate()}
                    />
                  ) : (
                    <InsertBlocks
                      ctaTemplates={loaded.ctaTemplates.items}
                      supervisors={loaded.supervisors.items}
                      onInsert={insertAtCaret}
                    />
                  )}
                </div>
              </aside>
            )}
          </div>

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          {saved && !error && (
            <p className="muted small" role="status">
              保存しました。
            </p>
          )}

          <div className="editpanel__actions">
            <button className="btn" type="submit" disabled={saving}>
              {saving ? '保存中…' : '保存する'}
            </button>
            {/*
              Said plainly, because "did that save?" is the question an author
              asks every few minutes once autosave exists. Three states and no
              ambiguity between them.
            */}
            {autosaving ? (
              <span className="muted small" role="status">
                自動保存中…
              </span>
            ) : dirty ? (
              <span className="muted small">未保存の変更があります（自動保存されます）</span>
            ) : (
              <span className="muted small">すべて保存済み</span>
            )}
          </div>
      </Card>

      {/*
        Rewriting one passage rather than the whole article: the writer has read
        it and knows which paragraph is wrong, and re-generating five thousand
        characters to fix one of them throws away everything they kept.

        At the passage, not in a card two screens below it - see the note at the
        top of RewritePopover.tsx for why that distance was the feature's
        problem rather than an inconvenience.
      */}
      <RewritePopover
        selected={selected}
        anchor={selectionRect}
        style={style}
        onStyle={setStyle}
        note={rewriteNote}
        onNote={setRewriteNote}
        busy={rewriting}
        error={rewriteError}
        result={rewritten}
        onRun={() => void runRewrite()}
        onApply={applyRewrite}
        onDiscard={() => setRewritten(null)}
        onClose={() => {
          setSelectionRect(null);
          setRewritten(null);
          setRewriteError(null);
        }}
      />

      {/*
        What is measurably wrong, before anybody reads four thousand characters
        to find it. Free and instant, so it sits open rather than behind a
        button - a check somebody has to ask for is a check nobody runs.

        BELOW the editor, beside the legal check, because that is when it is
        read. It used to sit between the approved material and the outline,
        which put a list of faults in the middle of the form that produces them
        - a writer scrolled past 「キーワードが本文にありません」 on the way to the
        text they were about to write, and read the same list again afterwards
        with no way to tell which pass they were on. The two checks are one
        question - is this ready - and they are now in one place.
      */}
      <Card
        title="文章チェック"
        caption="キーワード・文字数・重複・構成の自動チェックです。AIは使いません。"
        actions={
          problems === 0 ? (
            <Pill tone="ok">問題なし</Pill>
          ) : (
            <Pill tone="warn">要確認 {problems}件</Pill>
          )
        }
      >
        {checks.length === 0 ? (
          <Empty>本文を書くとチェックが表示されます。</Empty>
        ) : (
          <ul className="findings">
            {checks.map((finding) => (
              <li
                key={finding.id}
                className={`findings__item${
                  finding.level === 'error'
                    ? ' findings__item--error'
                    : finding.level === 'warn'
                      ? ' findings__item--warn'
                      : ''
                }`}
              >
                <strong className="findings__title">{finding.title}</strong>
                <span className="muted small">{finding.detail}</span>
                {/*
                  At the FOOT of the tile, like the legal panel's.
                  
                  They used to sit to the right of the text on the same line, so
                  a two-line finding put its buttons beside the first line and a
                  one-line finding put them level with everything - the same two
                  controls landing at a different height on every row. Two panels
                  of findings sit on this screen and they now read as one kind of
                  thing.
                  
                  Only where there is a place to go. A missing keyword has no
                  location - that is what makes it missing.
                */}
                {finding.quote && (
                  <span className="findings__actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => jumpToText(finding.quote!)}
                    >
                      該当箇所へ移動
                    </button>
                    {/*
                      A check that only says what is wrong leaves the writer to
                      work out what to write instead. This proposes it; applying
                      it is still their press.
                    */}
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={rewriting}
                      onClick={() =>
                        fixFinding({
                          quote: finding.quote!,
                          title: finding.title,
                          guidance: finding.detail,
                        })
                      }
                    >
                      AIで直す
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/*
        法令チェック.

        Two halves behind one panel: the deterministic expression rules, and a
        reading of the released legal corpus whose every citation the server has
        verified against the provision it names. See modules/legal/review.ts.

        It never says 適合. These checks can show that something looks wrong;
        only a person can say an article is lawful, and a screen that claims
        otherwise invites a clinic to stop reading.
      */}
      <LegalReview
        articleId={article.id}
        state={review}
        busy={rewriting}
        onFix={fixFinding}
        onJump={(_at, quote) => {
          /*
           * By the words, not by the offset the server sent. It verified the
           * quote occurs in the body it reviewed, and the body may have been
           * edited since - a search finds the sentence wherever it moved to, and
           * finds nothing rather than the wrong paragraph if it is gone.
           */
          jumpToText(quote);
        }}
        onChanged={async () => {
          setReview(await get<ArticleReview>(`/articles/${article.id}/review`));
        }}
      />

      {/*
        The article's own settings, and everything the generator may use, in one
        card at the FOOT of the screen.

        They used to open it: eight controls and two pickers above the text, so
        the first thing anybody met on the way to writing was a form. By the time
        an article reaches this screen those decisions are made - the wizard asked
        for them, the plan screen confirmed them - and what is left is occasional
        correction. Still editable, still saved by the same 保存する button, and no
        longer standing between the writer and the article.
      */}
      <Card
        title="記事の条件と使用情報"
        caption="ここでの変更は「保存する」で反映されます。生成には承認済みの情報のみを使います。"
      >
        <p className="form__section" id="brief">
          記事の条件
        </p>
        <div className="editpanel__body">
          <Field label="状態" htmlFor="a-status">
            <Select
              id="a-status"
              value={status}
              // Select hands back a plain string; the state is the narrower
              // union, and the options are exactly its members.
              onChange={(next) => setStatus(next as ArticleDetail['status'])}
              options={
                // ARCHIVED is reached through the button, not chosen from a list.
                article.status === 'ARCHIVED'
                  ? [...STATUS_OPTIONS, { value: 'ARCHIVED', label: 'アーカイブ' }]
                  : STATUS_OPTIONS
              }
            />
          </Field>
          <Field label="公開想定日" htmlFor="a-publish" hint="空欄にすると未定に戻します。">
            <DateInput id="a-publish" value={publishOn} onChange={setPublishOn} />
          </Field>
          {/*
            Editable here as well as in the wizard: the keywords an article
            targets are the first thing a review changes, and until now the
            only way to change them after creation was the API.
          */}
          {/*
            The one keyword the article is written to rank for, kept apart from
            the set above: the search intent, the title candidates and the
            regional pairings are all read from a single term, and the first
            entry of a list is whichever one somebody typed first.
          */}
          <Field label="記事タイプ" htmlFor="a-type">
            <Select
              id="a-type"
              value={articleType}
              onChange={setArticleType}
              options={ARTICLE_TYPES}
            />
          </Field>
          <Field
            label="文体"
            htmlFor="a-tone"
            hint="この記事だけの指定です。空欄はクリニックの標準。"
          >
            <input
              id="a-tone"
              maxLength={200}
              value={tone}
              placeholder="クリニックの標準"
              onChange={(e) => setTone(e.target.value)}
            />
          </Field>
          <Field label="目安文字数" htmlFor="a-target-length" hint="空欄で既定（4,000〜5,000字）。">
            <input
              id="a-target-length"
              type="number"
              min={500}
              max={20000}
              step={100}
              value={targetLength}
              placeholder="4000"
              onChange={(e) => setTargetLength(e.target.value)}
            />
          </Field>
          <Field
            label="キーワード"
            htmlFor="a-keywords"
            span="full"
            hint="1つ目が狙うキーワードです。ほかのタグを押すと入れ替わります。"
          >
            <KeywordInput id="a-keywords" value={keywords} onChange={setKeywords} primary />
          </Field>
        </div>

        {/*
          The material the article is written from, chosen here rather than
          only at creation: a draft written before its treatment was approved
          had no way to pick one up, which made 本文を生成 permanently
          unreachable for it.

          Only APPROVED rows in this store's scope appear - the same set
          articles/context.ts reads - so a choice offered here is a choice the
          generator will accept.
        */}
        {/*
          The material this article is written from, chosen here as well as on
          the plan screen: a draft written before its treatment was approved had
          no way to pick one up, which made 本文を生成 permanently unreachable.
        */}
        <p className="form__section">使用する承認済み情報</p>
        <div className="editpanel__body">
          <MaterialPicker idPrefix="a" value={material} onChange={setMaterial} options={loaded} />
        </div>


        {/*
          The outline, above the text it describes.

          After the plan screen the structure used to disappear: this is where
          it becomes permanent - what the article is made of, how long each
          part is against its target, and the ability to redraw one section
          without touching the rest.
        */}

        <p className="form__section">生成に使用する情報</p>

        {context.missing.length > 0 && (
          <ul className="feed" style={{ marginBottom: 12 }}>
            {context.missing.map((item) => (
              <li key={item}>
                <span className="feed__dot" style={{ background: 'var(--warn)' }} />
                {MISSING_LABEL[item] ?? item}
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid--3">
          <div className="stat">
            <div className="stat__label">文体（適用値）</div>
            <div className="stat__value stat__value--sm">{context.resolved.tone}</div>
            <div className="muted small">
              クリニック既定：{context.clinic.defaultTone}
            </div>
          </div>
          <div className="stat">
            <div className="stat__label">サイトURL（適用値）</div>
            <div className="stat__value stat__value--sm">
              {context.resolved.siteUrl ?? '—'}
            </div>
            <div className="muted small">{context.clinic.siteName || 'サイト名未登録'}</div>
          </div>
          <div className="stat">
            <div className="stat__label">キーワード</div>
            <div className="stat__value stat__value--sm">
              {new Set([...context.article.keywords, ...context.resolved.keywords]).size}
            </div>
            <div className="muted small">記事＋クリニック既定</div>
          </div>
        </div>

        {/*
          The keywords as tags, across the row.

          Joined with 「・」 they were a paragraph inside a tile: a clinic writing
          long-tail Japanese registers a dozen, and a dozen of them rendered as
          one grey sentence under a number is something you have to READ to
          count. The first is marked because it is the one the article is
          written to rank for; the count above is de-duplicated, because the
          article's own list and the clinic default overlap and the sum of the
          two was reporting more keywords than exist.
        */}
        {[...new Set([...context.article.keywords, ...context.resolved.keywords])].length > 0 && (
          <div className="taglist" style={{ marginTop: 12 }}>
            {[...new Set([...context.article.keywords, ...context.resolved.keywords])].map(
              (word) => (
                <span
                  key={word}
                  className={`tag${word === context.article.keywords[0] ? ' tag--primary' : ''}`}
                >
                  {word}
                </span>
              ),
            )}
          </div>
        )}

        {/*
          The place and the operator, because the generator now receives them and
          this panel's only job is to be complete. A field left blank here is a
          field the article cannot mention - which is the useful half of showing
          it: 「未登録」 is where an operator finds out why the access paragraph
          they expected is missing.
        */}
        <p className="form__section">店舗・運営情報</p>
        <dl className="deflist">
          <div>
            <dt className="muted small">住所</dt>
            <dd>
              {[context.store.postalCode && `〒${context.store.postalCode}`, context.store.address]
                .filter(Boolean)
                .join(' ') || <span className="muted">未登録</span>}
            </dd>
          </div>
          <div>
            <dt className="muted small">アクセス</dt>
            <dd>{context.store.accessNote || <span className="muted">未登録</span>}</dd>
          </div>
          <div>
            <dt className="muted small">診療時間</dt>
            <dd>
              {context.store.hoursNote || <span className="muted">未登録</span>}
              {context.store.closedNote && (
                <span className="muted small">（休診：{context.store.closedNote}）</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="muted small">予約・電話</dt>
            <dd>
              {[context.store.phone, context.store.reservationUrl].filter(Boolean).join(' / ') || (
                <span className="muted">未登録</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="muted small">運営法人</dt>
            <dd>
              {context.clinic.corporateName || <span className="muted">未登録</span>}
              {context.clinic.representative && (
                <span className="muted small">代表者：{context.clinic.representative}</span>
              )}
            </dd>
          </div>
        </dl>

        <p className="form__section">引用できる情報</p>
        <ul className="feed">
          <li>
            <span className="feed__dot" />
            ペルソナ
            <span className="feed__count">{context.persona?.label ?? '未選択'}</span>
          </li>
          <li>
            <span className="feed__dot" />
            施術
            <span className="feed__count">{context.treatment?.name ?? '未選択'}</span>
          </li>
          <li>
            <span className="feed__dot" />
            CTA
            <span className="feed__count">{context.cta?.name ?? '未選択'}</span>
          </li>
          <li>
            <span className="feed__dot" />
            監修医
            <span className="feed__count">
              {context.supervisors.map((s) => s.name).join('・') || 'なし'}
            </span>
          </li>
          {Object.entries(context.strengths).map(([category, rows]) => (
            <li key={category}>
              <span className="feed__dot" />
              {CATEGORY_LABEL[category] ?? category}
              <span className="feed__count">{rows.length}</span>
            </li>
          ))}
        </ul>

        {context.article.generationNote && (
          <>
            <p className="form__section">指示（生成時に使用）</p>
            <p className="muted small" style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
              {context.article.generationNote}
            </p>
          </>
        )}
      </Card>
      </form>
    </>
  );
}
